/**
 * Assessment Store - Central state management for the assessment flow
 *
 * Uses Zustand for lightweight, reactive state management.
 * Tracks the entire assessment lifecycle from login through results.
 *
 * KEY SECTIONS:
 * - Flow Control: Which step the user is on
 * - Learner Info: User identity and session
 * - Authentication: Face enrollment status
 * - Assessment: Questions, responses, timing
 * - Proctoring: Face verification and integrity scoring
 * - Results: Final proficiency determination
 */

import { create } from 'zustand'
import {
  AssessmentStep,
  Learner,
  UserResponse,
  ProctoringEvent,
  ProctoringStatus,
  AssessmentResult,
  AvatarState,
} from '../types'
import type { IntegrityCompletionStatus, AssessmentReportRecord } from '@really-assess/shared/integrations'
import { getProctoringConfig } from './proctoringConfigStore'
import type { ProctoringConfig } from './proctoringConfigStore'
import { BROWSER_LOCKDOWN_VIOLATION_TYPES } from '@really-assess/shared/proctoring'
import { useAssessmentDefinitionStore } from './assessmentDefinitionStore'

// ─────────────────────────────────────────────────────────────────────────────
// Initial State Values
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_PROCTORING_STATUS: ProctoringStatus = {
  faceDetected: false,
  identityMatch: 0,
  violations: [],
  score: 100, // Start at 100, deduct for violations
}

const INITIAL_AVATAR_STATE: AvatarState = {
  isReady: false,
  isSpeaking: false,
  audioUnlocked: false,
  isMinimized: false,
  conversationCount: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Interface
// ─────────────────────────────────────────────────────────────────────────────

interface AssessmentState {
  // Flow control
  currentStep: AssessmentStep
  setStep: (step: AssessmentStep) => void

  // Learner info
  learner: Learner | null
  setLearner: (learner: Learner) => void
  updateLearner: (updates: Partial<Learner>) => void

  // Session management (server-side session tracking)
  sessionId: string | null
  setSessionId: (id: string | null) => void
  sessionToken: string | null
  setSessionToken: (token: string | null) => void

  // Authentication (face enrollment)
  isIdVerified: boolean
  setIdVerified: (verified: boolean) => void
  isFaceEnrolled: boolean
  setFaceEnrolled: (enrolled: boolean) => void
  faceEncodingId: string | null
  setFaceEncodingId: (id: string) => void

  // Assessment progress
  currentQuestionIndex: number
  setCurrentQuestionIndex: (index: number) => void
  responses: UserResponse[]
  addResponse: (response: UserResponse) => void
  updateResponse: (questionId: string, updates: Partial<UserResponse>) => void
  assessmentStartTime: number | null
  setAssessmentStartTime: (time: number) => void

  // Proctoring
  proctoringStatus: ProctoringStatus
  updateProctoringStatus: (updates: Partial<ProctoringStatus>) => void
  addProctoringEvent: (event: ProctoringEvent) => void
  verificationInterval: NodeJS.Timeout | null
  setVerificationInterval: (interval: NodeJS.Timeout | null) => void

  // Avatar state
  avatarState: AvatarState
  setAvatarState: (updates: Partial<AvatarState>) => void

  // Assessment completion (triggered by external system)
  assessmentCompleted: boolean
  setAssessmentCompleted: (completed: boolean) => void
  completionOutcome: IntegrityCompletionStatus | null
  setCompletionOutcome: (outcome: IntegrityCompletionStatus | null) => void
  timeLimitStartedAt: number | null
  setTimeLimitStartedAt: (time: number | null) => void

  // Results
  result: AssessmentResult | null
  calculateResult: () => void
  /** Structured report from ReallyAvatar (Overall Competency / The Why / TSC) */
  assessmentReport: AssessmentReportRecord | null
  setAssessmentReport: (report: AssessmentReportRecord | null) => void

  // Reset for new assessment
  resetAssessment: () => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Proctoring Score Calculation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculate composite proctoring integrity score.
 *
 * Uses configurable weights and penalties from proctoringConfigStore.
 * Adjust values at /proctoring-test page, they persist to localStorage.
 *
 * DEFAULT SCORING WEIGHTS:
 * - 70% Identity Match: How confident we are this is the enrolled user
 * - 15% Face Presence: Is a face currently visible in the camera
 * - 15% Behavioral: Deductions for violations (tab switching, etc.)
 *
 * DEFAULT VIOLATION PENALTIES (configurable):
 * - no_face: -1 point each
 * - multiple_faces: -3 points each (severe)
 * - identity_mismatch: -2 points each
 * - tab_switch: -2 points each
 * - window_blur: -1 point each
 */
export function isBrowserLockdownViolation(type: ProctoringEvent['type']): boolean {
  return (BROWSER_LOCKDOWN_VIOLATION_TYPES as readonly string[]).includes(type)
}

export function calculateProctoringScore(
  status: ProctoringStatus,
  config: Pick<ProctoringConfig, 'scoringWeights' | 'violationPenalties' | 'browserLockdown'> = getProctoringConfig()
): number {
  const { scoringWeights, violationPenalties } = config

  const identityScore = status.identityMatch // 0-100
  const facePresenceScore = status.faceDetected ? 100 : 0

  // Calculate behavioral penalty from violations using configurable penalties
  let behavioralPenalty = 0
  status.violations.forEach((violation) => {
    // Tab/blur/fullscreen only count when the assessment requires browser lockdown
    if (!config.browserLockdown && isBrowserLockdownViolation(violation.type)) return
    switch (violation.type) {
      case 'no_face':
        behavioralPenalty += violationPenalties.noFace
        break
      case 'multiple_faces':
        behavioralPenalty += violationPenalties.multipleFaces
        break
      case 'identity_mismatch':
        behavioralPenalty += violationPenalties.identityMismatch
        break
      case 'tab_switch':
        behavioralPenalty += violationPenalties.tabSwitch
        break
      case 'window_blur':
        behavioralPenalty += violationPenalties.windowBlur
        break
      case 'fullscreen_exit':
        behavioralPenalty += violationPenalties.fullscreenExit
        break
    }
  })
  const behavioralScore = Math.max(0, 100 - behavioralPenalty)

  // Weighted composite using configurable weights, clamped to 0-100
  const rawScore =
    identityScore * scoringWeights.identity +
    facePresenceScore * scoringWeights.presence +
    behavioralScore * scoringWeights.behavior

  return Math.min(100, Math.max(0, Math.round(rawScore)))
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Implementation
// ─────────────────────────────────────────────────────────────────────────────

export const useAssessmentStore = create<AssessmentState>((set, get) => ({
  // ─────────────────────────────────────────────────────────────────
  // Flow Control
  // ─────────────────────────────────────────────────────────────────

  currentStep: 'login',
  setStep: (step) => set({ currentStep: step }),

  // ─────────────────────────────────────────────────────────────────
  // Learner Info
  // ─────────────────────────────────────────────────────────────────

  learner: null,
  setLearner: (learner) => set({ learner }),
  updateLearner: (updates) => set((state) => ({
    learner: state.learner ? { ...state.learner, ...updates } : null,
  })),

  // ─────────────────────────────────────────────────────────────────
  // Session Management
  // ─────────────────────────────────────────────────────────────────

  sessionId: null,
  setSessionId: (id) => set({ sessionId: id }),
  sessionToken: null,
  setSessionToken: (token) => set({ sessionToken: token }),

  // ─────────────────────────────────────────────────────────────────
  // Authentication
  // ─────────────────────────────────────────────────────────────────

  isIdVerified: false,
  setIdVerified: (verified) => set({ isIdVerified: verified }),
  isFaceEnrolled: false,
  setFaceEnrolled: (enrolled) => set({ isFaceEnrolled: enrolled }),
  faceEncodingId: null,
  setFaceEncodingId: (id) => set({ faceEncodingId: id }),

  // ─────────────────────────────────────────────────────────────────
  // Assessment Progress
  // ─────────────────────────────────────────────────────────────────

  currentQuestionIndex: 0,
  setCurrentQuestionIndex: (index) => set({ currentQuestionIndex: index }),

  responses: [],
  addResponse: (response) => set((state) => ({
    responses: [...state.responses, response],
  })),
  updateResponse: (questionId, updates) => set((state) => ({
    responses: state.responses.map((r) =>
      r.questionId === questionId ? { ...r, ...updates } : r
    ),
  })),

  assessmentStartTime: null,
  setAssessmentStartTime: (time) => set({ assessmentStartTime: time }),

  // ─────────────────────────────────────────────────────────────────
  // Proctoring
  // ─────────────────────────────────────────────────────────────────

  proctoringStatus: INITIAL_PROCTORING_STATUS,

  /** Update proctoring status and recalculate integrity score */
  updateProctoringStatus: (updates) => set((state) => {
    const newStatus = { ...state.proctoringStatus, ...updates }
    return {
      proctoringStatus: {
        ...newStatus,
        score: calculateProctoringScore(newStatus),
      },
    }
  }),

  /** Add a proctoring violation event and recalculate score */
  addProctoringEvent: (event) => set((state) => {
    // Lockdown-only events are not recorded when the assessment disables lockdown
    if (isBrowserLockdownViolation(event.type) && !getProctoringConfig().browserLockdown) {
      return {}
    }
    const newStatus = {
      ...state.proctoringStatus,
      violations: [...state.proctoringStatus.violations, event],
    }
    return {
      proctoringStatus: {
        ...newStatus,
        score: calculateProctoringScore(newStatus),
      },
    }
  }),

  verificationInterval: null,
  setVerificationInterval: (interval) => set({ verificationInterval: interval }),

  // ─────────────────────────────────────────────────────────────────
  // Avatar State
  // ─────────────────────────────────────────────────────────────────

  avatarState: INITIAL_AVATAR_STATE,
  setAvatarState: (updates) => set((state) => ({
    avatarState: { ...state.avatarState, ...updates },
  })),

  // ─────────────────────────────────────────────────────────────────
  // Results Calculation
  // ─────────────────────────────────────────────────────────────────

  assessmentCompleted: false,
  setAssessmentCompleted: (completed) => set({ assessmentCompleted: completed }),
  completionOutcome: null,
  setCompletionOutcome: (outcome) => set({ completionOutcome: outcome }),
  timeLimitStartedAt: null,
  setTimeLimitStartedAt: (time) => set({ timeLimitStartedAt: time }),

  result: null,
  assessmentReport: null,
  setAssessmentReport: (report) => set({ assessmentReport: report }),

  /** Calculate final result — integrity score is the primary outcome. */
  calculateResult: () => {
    const state = get()
    if (!state.learner) return

    const totalTimeSeconds = state.timeLimitStartedAt
      ? Math.floor((Date.now() - state.timeLimitStartedAt) / 1000)
      : state.assessmentStartTime
        ? Math.floor((Date.now() - state.assessmentStartTime) / 1000)
        : 0

    const config = getProctoringConfig()
    const integrityScore = state.proctoringStatus.score
    const outcome = state.completionOutcome ?? 'completed'

    let passed = false
    let failureReason: AssessmentResult['failureReason']

    if (outcome === 'timed_out') {
      passed = false
      failureReason = 'time_limit_exceeded'
    } else if (outcome === 'suspended') {
      passed = false
      failureReason = 'proctoring_suspended'
    } else {
      passed = integrityScore >= config.passingScore
      if (!passed) {
        failureReason = 'integrity_below_threshold'
      }
    }

    const assessmentDefinition = useAssessmentDefinitionStore.getState()
    const assessmentTitle =
      assessmentDefinition.definition?.title ?? assessmentDefinition.slug ?? 'Assessment'

    const result: AssessmentResult = {
      learnerId: state.learner.id,
      learnerName: state.learner.name,
      proficiencyLevel: 'novice-1',
      proficiencyLabel: 'Integrity Assessment',
      description: 'Proctored assessment integrity evaluation',
      dateTaken: new Date(),
      responses: state.responses,
      proctoringScore: integrityScore,
      proctoringViolations: state.proctoringStatus.violations,
      passed,
      failureReason,
      completionStatus: outcome,
      assessmentTitle,
      timeSpent: totalTimeSeconds,
      questionsAnswered: 0,
      questionsTotal: 0,
    }

    set({ result })
  },

  // ─────────────────────────────────────────────────────────────────
  // Reset
  // ─────────────────────────────────────────────────────────────────

  /** Reset all state for a new assessment */
  resetAssessment: () => set({
    currentStep: 'login',
    learner: null,
    sessionId: null,
    sessionToken: null,
    isIdVerified: false,
    isFaceEnrolled: false,
    faceEncodingId: null,
    currentQuestionIndex: 0,
    responses: [],
    assessmentStartTime: null,
    proctoringStatus: INITIAL_PROCTORING_STATUS,
    avatarState: INITIAL_AVATAR_STATE,
    assessmentCompleted: false,
    completionOutcome: null,
    timeLimitStartedAt: null,
    result: null,
    assessmentReport: null,
  }),
}))
